import { useCallback, useEffect, useReducer, useState } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";

import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../u2000Core/hooks/handler/U2000UseGetHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import type { U2000SearchPayload, U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { NS, U3101ROLES } from "../U3101";
import U3101EmployeeBody from "./form/u3101Employee/U3101EmployeeBody";
import { U3101EmployeeFormToolBar } from "./form/u3101Employee/U3101EmployeeFormToolBar";
import U3101EmployeeDeleteDlg from "./common/U3101EmployeeDeleteDlg";
import U3101HourBankHistoryTab from "./form/u3101Employee/U3101HourBankHistoryTab";
import U3101SalaryHistoryTab from "./form/u3101Employee/U3101SalaryHistoryTab";
import { useU3101Context } from "../U3101Context";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import { u3101CollectEmployeeFormErrors } from "../tools/U3101EmployeeFormValidation";
import {
  doCreateData,
  doDeleteData,
  doGetAuditsData,
  doGetData,
  doSearch,
  doUpdateData,
  emptyEmployee,
  luHandleAppiError,
  normalizeU3101EmployeeFromApi,
  u3101SalaryHistoryRemunerationLocked,
  type U3101Employee,
  type U3101SalaryHistory,
} from "../U3101Service";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";
import { useU3101SalaryHistoryHandlers } from "../U3101UseSalaryHistoryHandlers";

// Interface à placer en dehors du composant pour éviter l'erreur de modificateur
export interface U3101EmployeeFormExtendedHandlers extends U2000FormStateHandlersReturn<U3101Employee> {
  addNewEmployee: () => void;
  modifyEmployee: (employee: U3101Employee) => void;
  deleteEmployee: (id: string) => Promise<void>;
}

const U3101EmployeeForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  // manage active tab state
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [remunerationLocked, setRemunerationLocked] = useState(false);
  const { fetch: fetchSalaryHistory } = useU3101SalaryHistoryHandlers();

  const refreshSalaryHistoryMeta = useCallback(
    async (employeeIdForHistory: string) => {
      const history = await fetchSalaryHistory(employeeIdForHistory);
      setRemunerationLocked(u3101SalaryHistoryRemunerationLocked(history));
    },
    [fetchSalaryHistory],
  );

  const handleSalaryHistoryChanged = useCallback((history: U3101SalaryHistory) => {
    setRemunerationLocked(u3101SalaryHistoryRemunerationLocked(history));
  }, []);

  // Form state and reducer for u3101Employee
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  // navigate et location
  const location = useLocation();
  const navigate = useNavigate();

  // Translation
  const { t } = useTranslation();
  const toast = useToast();

  // API Axios Client
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  // LU Handler,  Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3101Context();

  // Form Handlers and default methods
  const validateForm = (employee: U3101Employee) => u3101CollectEmployeeFormErrors(employee, t);

  const u3101EmployeeBL: U2000FormSBusinessHandlers<U3101Employee> = {
    newRecord: () => {
      return emptyEmployee;
    },

    // validation du formulaire
    validateForm: validateForm,

    // sauvegarde du formulaire
    saveForme: async (employee: U3101Employee, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(employee);
      if (Object.keys(errors).length === 0) {
        handleSave.saveData(employee, editMode);
      } else {
        u3101EmployeeFormStateExtendedHandlers.setErrors(errors);
      }
    },

    // annulation du formulaire
    cancelForm: () => {
      const employee = u3101EmployeeFormStateExtendedHandlers.getOldData() as U3101Employee | null;
      if (employee) {
        u3101EmployeeFormStateExtendedHandlers.setData({ ...employee });
      }

      if (luStateHandlers.getActionOriginFrom() === "/U3101") {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    // gestion du changement d'enregistrement
    onChangeRecordForm: (employee) => {
      if (employee.id) {
        handleGet.getData(employee.id);
      }
    },

    // gestion du changement de page
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },

    // gestion de la sortie du formulaire
    onLeaveForm: () => {
      navigate("/U3101");
    },
  };

  const u3101EmployeeFormStateHandlers = useU2000FormStateHandlers<U3101Employee>({
    formState,
    formStateDispatch,
    businessHandlers: u3101EmployeeBL,
  });

  // extended methods
  const u3101EmployeeFormStateExtendedHandlers: U3101EmployeeFormExtendedHandlers = {
    ...u3101EmployeeFormStateHandlers,

    addNewEmployee: () => {
      u3101EmployeeFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    },
    modifyEmployee: () => {
      u3101EmployeeFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteEmployee: async (_id: string) => {
      handleDelete.handleDelete();
    },
  };

  // Call Search Handlers for Logical Unit
  const handleSearch = useU2000SearchHandlers<U3101Employee>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3101Employee>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Get Handlers for Logical Unit
  const handleGet = useU2000GetHandlers<U3101Employee & { id: string }>({
    doGetFn: (id: string) => {
      return doGetData(u2000AxiosAuthenticated, id, true);
    },
    onSuccess: (employee: U3101Employee & { id: string }) => {
      const normalized = normalizeU3101EmployeeFromApi(employee);
      gridStateHandlers.setDataById(normalized.id, normalized);
      u3101EmployeeFormStateExtendedHandlers.setData(normalized);

      // Remplace l'id dans l'URL par le nouvel id
      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(employee.id));
      navigate(newPath, { replace: true });
      setLogicalUnitBreadcrumb([
        { href: "/U3101", label: t("title", { ns: NS }) },
        { label: (normalized.firstName || "") + " " + (normalized.lastName || "") + (normalized.codeEmployee ? " (" + normalized.codeEmployee + ")" : "") },
      ]);

      // Détermine le mode du formulaire selon l'action précédente
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3101EmployeeFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Save Handlers for Logical Unit
  const handleSave = useU2000SaveHandlers<U3101Employee>({
    doSaveFn: (employee: U3101Employee, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, employee);
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, employee.id, employee);
      }
      // Ensure a Promise is always returned to satisfy the handler's expected signature
      else return Promise.resolve(undefined);
    },
    onSuccess: (employee: U3101Employee & { id: string }, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), employee]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        gridStateHandlers.setCurrentIdx(gridStateHandlers.getData().length);

        // Remplace l'id dans l'URL par le nouvel id
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(employee.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(employee.id, employee);
      }
      u3101EmployeeFormStateExtendedHandlers.afterSaveData(employee);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));

      // Retourne à la liste après l'ajout ou la modification selon l'origine
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === "/U3101") {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);

      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3101EmployeeFormStateExtendedHandlers.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3101EmployeeFormStateExtendedHandlers.setErrors(errorResult.fieldErrors);
      }
    },
  });

  // Call Delete Handlers for Logical Unit
  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u3101EmployeeFormStateExtendedHandlers.getData()],
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, row);
    },

    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      let currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: row["codeEmployee"], ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      // Apres suppression, aller au prochain record, aller vers l'avant si possible, sinon aller vers l'arrière, si aucun, retourner à la liste
      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.PREVIOUSPAGE);
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate("/U3101", { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  // Initialisation au montage et quand id change (ex. F5 sur /U3101/:id : id peut arriver après le 1er rendu).
  useEffect(() => {
    if (id === undefined) return;
    if (id === "new") {
      u3101EmployeeFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
      u3101EmployeeFormStateExtendedHandlers.setData({ ...emptyEmployee });
      setRemunerationLocked(false);
    } else {
      handleGet.getData(id);
      void refreshSalaryHistoryMeta(id);
    }
  }, [id]);

  if (u3101EmployeeFormStateExtendedHandlers.getData() === null) {
    return null;
  }

  // Tabs du formulaire
  const employeeId = u3101EmployeeFormStateExtendedHandlers.getData()?.id;
  const showHistoryTabs =
    hasRole(U3101ROLES.READ) && Boolean(employeeId?.trim()) && id !== "new";

  const formTabsComponent: any[] = [
    showHistoryTabs && {
      id: "tabSalaryHistory",
      label: t("tabSalaryHistory", { ns: NS }),
      content: (
        <U3101SalaryHistoryTab
          employeeId={employeeId as string}
          onSalaryApplied={() => {
            if (employeeId?.trim()) {
              handleGet.getData(employeeId);
            }
          }}
          onHistoryChanged={handleSalaryHistoryChanged}
        />
      ),
    },
    showHistoryTabs && {
      id: "tabHourBankHistory",
      label: t("tabHourBankHistory", { ns: NS }),
      content: (
        <U3101HourBankHistoryTab
          employeeId={employeeId as string}
          onOpenYearInitialsChanged={() => {
            if (employeeId?.trim()) {
              handleGet.getData(employeeId);
            }
          }}
        />
      ),
    },
    hasRole(U3101ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: <U2000AuditPanelComponent id={u3101EmployeeFormStateExtendedHandlers.getData()?.id} doGetAuditsData={doGetAuditsData} toastName="U3101EmployeeAudit" />,
    },
  ].filter(Boolean);

  // Render
  return (
    <>
      <U3101EmployeeDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />

      <U2000DefaultForm
        actionsBtns={<U3101EmployeeFormToolBar formStateHandlers={u3101EmployeeFormStateExtendedHandlers as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3101EmployeeFormStateExtendedHandlers as any}
      >
        <U3101EmployeeBody
          formStateHandlers={u3101EmployeeFormStateExtendedHandlers as any}
          t={t}
          remunerationReadOnly={remunerationLocked}
        />

        {formTabsComponent.length > 0 && (
          <U2000Tab className="mt-4" tabs={formTabsComponent} defaultTab={formTabsComponent[0]?.id} activeTab={activeTab ?? formTabsComponent[0]?.id} setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))} />
        )}
      </U2000DefaultForm>
    </>
  );
};

export default U3101EmployeeForm;
